import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface KnowledgeCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface KnowledgeArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  category_id: string;
  tags: string[];
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  knowledge_categories?: {
    name: string;
    icon: string;
    color: string;
  };
}

export const KnowledgeBaseManagement: React.FC = () => {
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'categories' | 'articles'>('articles');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    summary: '',
    category_id: '',
    tags: [] as string[],
    priority: 0,
    is_active: true
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  const fetchCategories = async () => {
    try {
      const result = await callAdmin('get_knowledge_categories')
      if (result.success) setCategories(result.data as KnowledgeCategory[])
      else setError(result.error || 'Failed to fetch categories')
    } catch (err) {
      setError('Failed to fetch categories');
      console.error('Error fetching categories:', err);
    }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get_knowledge_articles', { data: { search: searchTerm, category_id: selectedCategory, limit: 50 } })
      if (result.success) {
        const data = result.data as { articles: KnowledgeArticle[], total: number, page: number, limit: number }
        setArticles(data.articles || [])
      } else {
        setError(result.error || 'Failed to fetch articles')
      }
    } catch (err) {
      setError('Failed to fetch articles');
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditArticle = (article: KnowledgeArticle) => {
    setSelectedArticle(article);
    setEditForm({
      title: article.title,
      content: article.content,
      summary: article.summary,
      category_id: article.category_id,
      tags: article.tags,
      priority: article.priority,
      is_active: article.is_active
    });
    setShowEditModal(true);
  };

  const handleCreateArticle = () => {
    setEditForm({
      title: '',
      content: '',
      summary: '',
      category_id: '',
      tags: [],
      priority: 0,
      is_active: true
    });
    setShowCreateModal(true);
  };

  const handleUpdateArticle = async () => {
    if (!selectedArticle) return;

    try {
      const result = await callAdmin('update_knowledge_article', { resourceId: selectedArticle.id, data: editForm })

      if (result.success) {
        setShowEditModal(false);
        fetchArticles();
      } else {
        setError(result.error || 'Failed to update article');
      }
    } catch (err) {
      setError('Failed to update article');
      console.error('Error updating article:', err);
    }
  };

  const handleCreateNewArticle = async () => {
    try {
      const result = await callAdmin('create_knowledge_article', { data: editForm })

      if (result.success) {
        setShowCreateModal(false);
        fetchArticles();
      } else {
        setError(result.error || 'Failed to create article');
      }
    } catch (err) {
      setError('Failed to create article');
      console.error('Error creating article:', err);
    }
  };

  const handleDeleteArticle = async (articleId: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      const result = await callAdmin('delete_knowledge_article', { resourceId: articleId })

      if (result.success) {
        fetchArticles();
      } else {
        setError(result.error || 'Failed to delete article');
      }
    } catch (err) {
      setError('Failed to delete article');
      console.error('Error deleting article:', err);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !editForm.tags.includes(newTag.trim())) {
      setEditForm({
        ...editForm,
        tags: [...editForm.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditForm({
      ...editForm,
      tags: editForm.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || article.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Knowledge Base Management</h2>
          <p className="text-gray-600">Manage AI platform instructions and resources</p>
        </div>
        <Button onClick={handleCreateArticle} className="px-6">
          Add New Article
        </Button>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('articles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'articles'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📚 Articles ({articles.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'categories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📁 Categories ({categories.length})
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'articles' && (
        <>
          {/* Filters */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <Button onClick={fetchArticles} className="px-6">
              Search
            </Button>
          </div>

          {/* Articles List */}
          <div className="space-y-4">
            {filteredArticles.map((article) => (
              <Card key={article.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-2xl">{article.knowledge_categories?.icon}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                        <p className="text-sm text-gray-600">{article.knowledge_categories?.name}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-3">{article.summary}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Priority: {article.priority}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        article.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {article.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span>Updated: {new Date(article.updated_at).toLocaleDateString()}</span>
                    </div>
                    {article.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {article.tags.map((tag, index) => (
                          <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditArticle(article)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteArticle(article.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <Card key={category.id} className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-3xl">{category.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {category.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-sm text-gray-500">
                  {articles.filter(a => a.category_id === category.id).length} articles
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Article Modal */}
      {(showEditModal || showCreateModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showCreateModal ? 'Create New Article' : 'Edit Article'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <Input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="w-full"
                    placeholder="Enter article title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={editForm.category_id}
                    onChange={(e) => setEditForm({...editForm, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <Input
                    type="number"
                    value={editForm.priority}
                    onChange={(e) => setEditForm({...editForm, priority: parseInt(e.target.value)})}
                    className="w-full"
                    placeholder="0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary
                  </label>
                  <textarea
                    value={editForm.summary}
                    onChange={(e) => setEditForm({...editForm, summary: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Enter article summary"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    value={editForm.content}
                    onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={10}
                    placeholder="Enter article content"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <div className="space-y-2">
                    {editForm.tags.map((tag, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="flex-1 text-sm text-gray-600">• {tag}</span>
                        <button
                          onClick={() => removeTag(tag)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className="flex space-x-2">
                      <Input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        className="flex-1"
                        placeholder="Add new tag"
                        onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      />
                      <Button onClick={addTag} className="px-4">
                        Add
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  onClick={() => {
                    setShowEditModal(false);
                    setShowCreateModal(false);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={showCreateModal ? handleCreateNewArticle : handleUpdateArticle}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
                >
                  {showCreateModal ? 'Create Article' : 'Update Article'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
